import { useTranslate } from "@similarweb/i18n-provider";
import type { ActivationAlertsContractDisplayAdAlert } from "@similarweb/pro-api-client";
import { useTracking } from "@similarweb/tracking-provider";
import { Badge, Carousel } from "@similarweb/ui-components";

import { AlertsGroupCarousel } from "../../common/AlertsGroupCarousel";
import { CardLayout } from "../../common/card-layout/CardLayout";
import { useSelectedGroupItemStore } from "../../common/modal/useSelectedGroupItemStore";
import { SeeAllButton } from "../../common/SeeAllButton";
import { SeeMoreCard } from "../../common/SeeMoreCard";
import { navigateToDisplayAds } from "../utils/navigateToAdsIntel";
import { adsTypeTranslationKeyMap } from "../utils/translationKeysConfig";

import { adCardSize, DisplayAdCard } from "./DisplayAdCard";

export interface IDisplayAdsAlertProps {
    groupIndex: number;
    country?: number;
    domain: string;
    favicon: string;
    ads: ActivationAlertsContractDisplayAdAlert[];
    totalAdsCount: number;
}

export const DisplayAdsAlert = ({
    groupIndex,
    country,
    domain,
    favicon,
    ads,
    totalAdsCount,
}: IDisplayAdsAlertProps) => {
    const { translate } = useTranslate();
    const { track } = useTracking();
    const setSelectedGroupItem = useSelectedGroupItemStore((store) => store.setSelectedGroupItem);

    const hasMoreAds = totalAdsCount > ads.length;

    const handleAdClick = (index: number) => {
        track("Alert Card", "click", "open alert/alerts/display-ads");
        setSelectedGroupItem({ itemIndex: index, groupIndex });
    };

    const handleNavigation = () => {
        navigateToDisplayAds({ domain, country });
    };

    const handleSeeAllClick = () => {
        track("Alert Card", "click", "see-all/alerts/display-ads");
        handleNavigation();
    };

    return (
        <CardLayout
            iconUrl={favicon}
            onClick={handleSeeAllClick}
            data-automation="display-ads-alert"
            data-domain={domain}
        >
            <CardLayout.Header>
                <Badge data-automation="alert-badge">{translate(adsTypeTranslationKeyMap.Display)}</Badge>
                <CardLayout.Title>
                    {translate("activation.alerts.cards.display-ads.title", {
                        count: totalAdsCount,
                        domain,
                    })}
                </CardLayout.Title>
            </CardLayout.Header>
            <CardLayout.Content>
                <AlertsGroupCarousel>
                    {ads.map((ad, index) => (
                        <Carousel.Item key={ad.id}>
                            <DisplayAdCard
                                ad={ad}
                                onClick={() => handleAdClick(index)}
                            />
                        </Carousel.Item>
                    ))}
                    {hasMoreAds ? (
                        <Carousel.Item key="see-more-card">
                            <SeeMoreCard
                                width={adCardSize.width}
                                height={adCardSize.height}
                                onClick={handleSeeAllClick}
                                dataAutomation="display-ad-see-more-card"
                            />
                        </Carousel.Item>
                    ) : null}
                </AlertsGroupCarousel>
                <SeeAllButton
                    onClick={handleSeeAllClick}
                    isDisabled={!ads.length}
                    dataAutomation="see-all-ads-button"
                >
                    {translate("activation.alerts.cards.ads.see-all")}
                </SeeAllButton>
            </CardLayout.Content>
        </CardLayout>
    );
};
